import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import { FaChartPie } from 'react-icons/fa';
import { ImInfo } from 'react-icons/im';
import {
  truncateCenter,
  EngineIcon,
  engineLabels
} from 'Utils';
import DiscoveryTooltip from 'DiscoveryTooltip';
import { isFileBasedDatasourceType } from 'Utils/fileTypeUtils';
import {
  buildOverviewRunStatLines,
  buildOverviewRunTime
} from './OverviewDonut';
import { FILE_TYPE_LABELS } from './OverviewPanel';

// Thin horizontal analogue of OverviewDonut's run-metadata card, used when a
// single-container run lands directly on its container's detail view (A1) and
// there's no Overview matrix to carry this context. Same facts, same links —
// laid out as a bar instead of a stacked card since it sits above a full-width
// detail panel rather than beside the donut. Never rendered for a "no sensitive
// data" run (the exact-path route only reaches this branch when
// totalSensitiveTableCount > 0), so — unlike OverviewDonut — no empty-state
// branch is needed here.
const RunMetadataBar = (props) => {
  const {
    connectionDetails,
    searchDate,
    sampleRules,
    runStats,
    engine,
    dbname,
    inProgress
  } = props;

  const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  const displayDate = (new Date(searchDate)).toLocaleDateString('en-US', dateOptions);
  const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
  const displayTime = (new Date(searchDate)).toLocaleString('en-US', timeOptions);
  const runStatLines = buildOverviewRunStatLines(inProgress, runStats);
  const runTime = buildOverviewRunTime(inProgress, runStats);

  const isFileSource = isFileBasedDatasourceType(connectionDetails?.datasourceType);
  const sourceDsType = connectionDetails?.datasourceType || null;
  const sourceFileTypeLabel = sourceDsType ? (FILE_TYPE_LABELS[sourceDsType] || sourceDsType) : '—';
  const sourceRootPath = connectionDetails?.rootPath || connectionDetails?.config?.rootPath || '—';
  const connectionId = connectionDetails?.id ?? null;

  return (
    <Card className="shadow-sm widget-card text-left mb-3">
      <div
        className="d-flex align-items-center p-3"
        style={{ flexWrap: 'wrap', columnGap: 24, rowGap: 8 }}
      >
        <div className='d-flex align-items-center' style={{ whiteSpace: 'nowrap' }}>
          <FaChartPie className="mr-1 widget-header-icon widget-header-icon-lg" />
          Discovery Overview
        </div>

        <div className="entity-date-label" style={{ fontSize: '12px' }}>
          Discovery ran on {displayDate} at {displayTime} using sampling rules {' '}
          <DiscoveryTooltip text='View sample rules details'>
            <Link to={`/rules/rules-editor/${encodeURIComponent(sampleRules)}`}>
              {sampleRules}
            </Link>
          </DiscoveryTooltip>
          .
          {runStatLines.length > 0 && (
            <DiscoveryTooltip
              placement='top'
              text={<div>{runStatLines.map((line, i) => <div key={i} className={i > 0 ? 'mt-1' : ''}>{line}</div>)}</div>}
            >
              <span className='ml-2'><ImInfo style={{ fontSize: '1.1em' }} color='#315DFF' /></span>
            </DiscoveryTooltip>
          )}
          {runTime && (
            <span className='ml-2'>({runTime})</span>
          )}
        </div>

        {connectionId != null && (
          <div style={{ whiteSpace: 'nowrap' }}>
            <DiscoveryTooltip text='View risk details' placement='top'>
              <Link to={`/landscape/riskprofile/${encodeURIComponent(connectionId)}`}>
                Risk Summary
              </Link>
            </DiscoveryTooltip>
          </div>
        )}

        {isFileSource ? (
          <>
            <div style={{ whiteSpace: 'nowrap' }}>
              <span className='mr-1 radial-card-meta-label'>Data Source:</span>
              <span style={{ fontSize: '14px' }}>
                {connectionId != null ? (
                  <DiscoveryTooltip text='View Data Source details' placement='top'>
                    <Link to={`/sources/source-editor/${encodeURIComponent(connectionId)}`}>
                      {connectionDetails?.name || `#${connectionId}`}
                    </Link>
                  </DiscoveryTooltip>
                ) : (
                  connectionDetails?.name || '—'
                )}
              </span>
            </div>
            <div style={{ whiteSpace: 'nowrap' }}>
              <span className='mr-1 radial-card-meta-label'>File Type:</span>
              <span style={{ fontSize: '14px' }}>{sourceFileTypeLabel}</span>
            </div>
            <div style={{ minWidth: 0 }}>
              <span className='mr-1 radial-card-meta-label'>Root path:</span>
              <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>
                {sourceRootPath}
              </span>
            </div>
          </>
        ) : (
          <>
            {connectionId != null && (
              <div style={{ whiteSpace: 'nowrap' }}>
                <DiscoveryTooltip text='View Data Source details' placement='top'>
                  <Link to={`/sources/source-editor/${encodeURIComponent(connectionId)}`}>
                    Data Source Details
                  </Link>
                </DiscoveryTooltip>
              </div>
            )}
            {dbname && (
              <div style={{ whiteSpace: 'nowrap' }}>
                <span className='mr-1 radial-card-meta-label text-capitalize'>
                  {engine === 's3' ? 'S3 Bucket:' : 'Database:'}
                </span>
                <span style={{ fontSize: '14px' }}>
                  {truncateCenter(dbname, 40, '..')}
                </span>
              </div>
            )}
            {engine && (
              <div className='d-flex align-items-center' style={{ whiteSpace: 'nowrap' }}>
                <span className='mr-1 radial-card-meta-label'>Engine:</span>
                <span style={{ fontSize: '14px' }}>
                  {engineLabels[engine]}
                  <EngineIcon classes='ml-2' size='1.2rem' engine={engine} />
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
};

export default RunMetadataBar;
